// Encryption Service for SAM
import { UserKeyService } from './userKeyService';

export class EncryptionService {
    /**
   * Encrypt sensitive data
   */
  static async encrypt(data: any): Promise<string> {
    try {
      const key = await UserKeyService.getKey();
      
      const jsonString = JSON.stringify(data);
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(jsonString);

      // Generate random IV
      const iv = crypto.getRandomValues(new Uint8Array(12));

      // Encrypt the data
      const encryptedBuffer = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        dataBuffer
      );

      // Combine IV and encrypted data
      const encryptedArray = new Uint8Array(encryptedBuffer);
      const combined = new Uint8Array(iv.length + encryptedArray.length);
      combined.set(iv);
      combined.set(encryptedArray, iv.length);

      // Convert to base64 string
      const encryptedString = btoa(String.fromCharCode(...Array.from(combined)));
      return encryptedString;
    } catch (error) {
      console.error('❌ Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }
  
  /**
   * Decrypt sensitive data
   */
  static async decrypt(encryptedData: string): Promise<any> {
    try {
      const key = await UserKeyService.getKey();
      
      // Convert base64 string back to Uint8Array
      const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
      
      // Extract IV and encrypted data
      const iv = combined.slice(0, 12);
      const encryptedArray = combined.slice(12);
      
      // Decrypt the data
      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        encryptedArray
      );
      
      // Convert back to JSON
      const decoder = new TextDecoder();
      const jsonString = decoder.decode(decryptedBuffer);
      return JSON.parse(jsonString);
    } catch (error) {
      // Silently handle decryption errors (likely old data with different key)
      // Don't spam console for end users
      throw new Error('Failed to decrypt data');
    }
  }
  
  /**
   * Check if data is encrypted
   */
  static isEncrypted(data: string): boolean {
    try {
      // Try to decode as base64
      const decoded = atob(data);
      // Check if it has the expected structure (IV + encrypted data)
      return decoded.length > 12;
    } catch {
      return false;
    }
  }
  
  /**
   * Encrypt specific sensitive fields from step data
   */
  static async encryptSensitiveData(data: any): Promise<{
    encryptedData: string;
    metadata: any;
  }> {
    // Separate sensitive and non-sensitive data
    const sensitiveData: any = {
      characterDefects: data.characterDefects,
      answers: data.answers,
      prayers: data.prayers,
      reflections: data.reflections,
      inventories: data.inventories,
      personalNotes: data.personalNotes,
      sponsorNotes: data.sponsorNotes
    };
    
    // Remove sensitive data from metadata
    const metadata: any = { ...data };
    delete metadata.characterDefects;
    delete metadata.answers;
    delete metadata.prayers;
    delete metadata.reflections;
    delete metadata.inventories;
    delete metadata.personalNotes;
    delete metadata.sponsorNotes;
    
    // Add encryption metadata
    metadata.encrypted = true;
    metadata.encryptedAt = new Date().toISOString();
    
    // Encrypt sensitive data
    const encryptedData = await this.encrypt(sensitiveData);
    
    return { encryptedData, metadata };
  }
  
  /**
   * Decrypt sensitive data and combine with metadata
   */
  static async decryptSensitiveData(encryptedData: string, metadata: any): Promise<any> {
    try {
      const sensitiveData = await this.decrypt(encryptedData);
      return {
        ...metadata,
        ...sensitiveData
      };
    } catch (error) {
      console.error('Failed to decrypt sensitive data:', error);
      // Return metadata only if decryption fails
      return metadata;
    }
  }
  
  /**
   * Test encryption/decryption cycle
   */
  static async testEncryption(): Promise<boolean> {
    try {
      const testData = {
        characterDefects: ['Selfishness', 'Dishonesty'],
        answers: { question1: 'I admitted I was powerless' },
        prayers: ['God, grant me the serenity...']
      };
      
      const encrypted = await this.encrypt(testData);
      const decrypted = await this.decrypt(encrypted);
      
      return JSON.stringify(testData) === JSON.stringify(decrypted);
    } catch (error) {
      console.error('Encryption test failed:', error);
      return false;
    }
  }
} 